import { useIsReadOnly } from '@/lib/casl'
import type {
  ProductEditorForm,
  ProductEditorSyncValidator,
  ProductEditorAsyncValidator,
} from '@/components/forms/ProductForm.types'
import type { ProductEditorValues, ProductFormVariant } from '@/lib/services/products/products.form'
import type { VariantFulfillmentType } from '@/lib/services/products/products.types'
import type { getDraftProductActivePriceAdjustment } from '@/lib/services/price-adjustments/priceAdjustments.summary'
import type { DeepKeys } from '@tanstack/react-form'
import { usePathHelper } from '@/hooks/usePathHelper'
import { getProductRole } from '@/lib/product-role'
import { getVariantLabel } from '@/lib/services/products/products.form'
import { getActivePriceAdjustmentPreviewLabel } from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { cn, readProperty } from '@/lib/utils'
import { Link } from '@tanstack/react-router'
import { useStore } from '@tanstack/react-form'
import { Boxes, Star, Trash2, Truck } from 'lucide-react'
import { useState } from 'react'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import ImageThumbnail from '@/components/media/ImageThumbnail'
import { PriceChangeInlineHint } from './PriceChangeInlineHint'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
export function getVariantRecipeSourceSignature(
  variants: readonly ProductFormVariant[] | null | undefined,
  currentIndex: number
): string {
  return (variants ?? [])
    .map((variant, variantIndex) => {
      if (variantIndex === currentIndex) {
        return ''
      }

      return [
        String(variant?.id ?? variantIndex),
        getVariantLabel(variant, variantIndex),
        String(variant?.recipe ?? ''),
      ].join('::')
    })
    .join('|||')
}

function getRecipeSources(
  variants: readonly ProductFormVariant[] | null | undefined,
  currentIndex: number
) {
  return (variants ?? [])
    .map((item, itemIndex) => ({
      index: itemIndex,
      label: getVariantLabel(item, itemIndex),
      recipe: String(item?.recipe ?? ''),
      recipe_html: item?.recipe_html ?? null,
    }))
    .filter((item) => item.index !== currentIndex && item.recipe.trim())
}

const FULFILLMENT_OPTIONS: { id: VariantFulfillmentType; name: string }[] = [
  { id: 'pickup', name: 'Pickup' },
  { id: 'delivery', name: 'Delivery' },
]

interface VariantRowProps {
  id: number | string
  index: number
  liveVariant?: Pick<ProductFormVariant, 'active_price_adjustment'> | null
  draftActivePriceAdjustment?: ReturnType<typeof getDraftProductActivePriceAdjustment>
  canTrackInventory?: boolean
  isSubscriptionPackage?: boolean
  showMinimumOptionNotice?: boolean
}

function VariantRow<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  form,
  id,
  index,
  liveVariant = null,
  draftActivePriceAdjustment = null,
  canTrackInventory = true,
  isSubscriptionPackage = false,
  showMinimumOptionNotice = true,
}: VariantRowProps & {
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
}) {
  const { isReadOnly } = useIsReadOnly()
  const path = usePathHelper()
  const inventorySetupPath = path.locations.create()
  const canDeleteVariant = useStore(form.store, (state) => (state.values.variants?.length ?? 0) > 1)
  const isDefaultVariant = useStore(form.store, (state) =>
    Boolean(state.values.variants?.[index]?.is_default)
  )
  const productMedia = useStore(form.store, (state) => state.values.media ?? [])
  useStore(form.store, (state) =>
    getVariantRecipeSourceSignature(state.values.variants ?? [], index)
  )
  const canViewPriceChangeLink = !isReadOnly
  const [recipeCopySelectKey, setRecipeCopySelectKey] = useState(0)
  const recipeSources = getRecipeSources(form.getFieldValue('variants') ?? [], index)
  const productDescription = useStore(form.store, (state) => state.values.description ?? '')
  const productDescriptionHtml = useStore(
    form.store,
    (state) => state.values.description_html ?? null
  )
  const productRole = useStore(form.store, (state) => getProductRole(state.values))
  const isSubscriptionTier = isSubscriptionPackage || productRole === 'subscription_package'
  const isAddOnProduct = productRole === 'standalone_add_on' || productRole === 'add_on_only'
  const isDesignersChoiceProduct = useStore(form.store, (state) =>
    Boolean(state.values.is_designers_choice)
  )
  const variantNoun = isSubscriptionTier ? 'Tier' : 'Variant'

  const clearFieldErrors = (fieldName: DeepKeys<ProductEditorValues>) => {
    form.setFieldMeta(fieldName, (meta) => {
      const errors = readProperty(meta, 'errors')
      const currentErrors = Array.isArray(errors) ? errors : []
      const currentErrorMap = meta?.errorMap ?? {}

      if (currentErrors.length === 0 && Object.keys(currentErrorMap).length === 0) {
        return meta
      }

      return {
        ...meta,
        errors: [],
        errorMap: {},
      }
    })
  }

  const handleInventoryTrackingChange = (
    checked: boolean,
    trackInventoryField: { setValue: (value: boolean) => void }
  ) => {
    const nextUseInventory = Boolean(checked)

    trackInventoryField.setValue(nextUseInventory)

    if (!nextUseInventory) {
      clearFieldErrors(`variants[${index}].on_hand`)
    }
  }

  const focusQuantityOnHandInput = () => {
    if (typeof window === 'undefined') {
      return
    }

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById(`${id}-variants.${index}.on_hand`)?.focus()
      })
    })
  }

  const setVariantAsDefault = (isDefault: boolean) => {
    const variants = form.getFieldValue('variants') ?? []

    if (isDefault) {
      form.setFieldValue(
        'variants',
        variants.map((item, itemIndex) => ({
          ...item,
          is_default: itemIndex === index,
        }))
      )

      return
    }

    const fallbackDefaultIndex = variants.findIndex((_, itemIndex) => itemIndex !== index)

    form.setFieldValue(
      'variants',
      variants.map((item, itemIndex) =>
        itemIndex === fallbackDefaultIndex
          ? { ...item, is_default: true }
          : itemIndex === index
            ? { ...item, is_default: fallbackDefaultIndex < 0 }
            : { ...item, is_default: false }
      )
    )
  }

  const removeVariant = () => {
    const variants = form.getFieldValue('variants') ?? []
    const nextVariants = variants.filter((_, itemIndex) => itemIndex !== index)

    if (!nextVariants.some((item) => item?.is_default) && nextVariants[0]) {
      nextVariants[0] = { ...nextVariants[0], is_default: true }
    }

    form.setFieldValue('variants', nextVariants)
  }

  const defaultProductMedia = productMedia[0] ?? null

  return (
    <div
      className={cn(
        'rounded-xl border border-border/60 bg-card p-5 transition-shadow',
        isDefaultVariant && 'border-primary/40'
      )}
      id={String(id)}
      data-read-only={isReadOnly}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Typography variant="eyebrow">{variantNoun}</Typography>
            {isDefaultVariant ? (
              <Badge variant="secondary" className="gap-1">
                <Star className="size-3 fill-current" />
                Default
              </Badge>
            ) : null}
          </div>

          {!isReadOnly ? (
            <Label
              htmlFor={`${id}-variants.${index}.default`}
              className="inline-flex w-fit items-center gap-3"
            >
              <Switch
                id={`${id}-variants.${index}.default`}
                name={`variants[${index}].is_default`}
                checked={isDefaultVariant}
                onCheckedChange={(checked) => setVariantAsDefault(Boolean(checked))}
              />
              <span className="text-sm font-medium">Default {variantNoun}</span>
            </Label>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="space-y-4 border-t border-border/60 pt-5">
            <div className="space-y-1">
              <Typography variant="label" className="block">
                Photo
              </Typography>
              <Typography variant="supporting" className="block">
                Choose which product photo goes with this {variantNoun.toLowerCase()}.
              </Typography>
            </div>

            {productMedia.length > 0 ? (
              <form.AppField name={`variants[${index}].media`}>
                {(subField) => {
                  const hasExplicitSelection = productMedia.some(
                    (media) => media.id === subField.state.value?.id
                  )
                  const selectedMediaId = hasExplicitSelection
                    ? subField.state.value?.id
                    : (defaultProductMedia?.id ?? null)

                  return (
                    <div className="flex flex-wrap gap-2">
                      {productMedia.map((media) => {
                        const isSelected = selectedMediaId === media.id

                        return (
                          <SelectableSurface
                            key={media.id}
                            type="button"
                            selected={isSelected}
                            radius="surface"
                            padding="none"
                            className="relative p-1"
                            disabled={isReadOnly}
                            onClick={() => subField.setValue(media)}
                          >
                            <ImageThumbnail
                              media={media}
                              className="w-[72px] rounded-xl shadow-none"
                            />
                            {isSelected ? (
                              <span className="absolute inset-x-1 bottom-1 rounded-lg bg-action px-2 py-1 text-[11px] font-medium text-action-foreground">
                                Selected
                              </span>
                            ) : null}
                          </SelectableSurface>
                        )
                      })}
                    </div>
                  )
                }}
              </form.AppField>
            ) : (
              <EmptyState
                size="compact"
                surface="transparent"
                title="Add product photos above to choose one here."
              />
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(12rem,0.9fr)]">
            <form.AppField name={`variants[${index}].name`}>
              {(subField) => (
                <subField.TextInput
                  id={`${id}-variants.${index}.name`}
                  label="Name"
                  placeholder="Standard, Deluxe, Premium, etc."
                />
              )}
            </form.AppField>

            <form.AppField name={`variants[${index}].price`}>
              {(subField) => (
                <>
                  <subField.MoneyInput
                    id={`${id}-variants.${index}.price`}
                    label="Price"
                    className="max-w-sm"
                  />
                  <form.Subscribe
                    selector={(state) => {
                      const variant = state.values.variants?.[index]

                      return {
                        formActivePriceAdjustment: variant?.active_price_adjustment,
                        hasFormActivePriceAdjustment: Object.prototype.hasOwnProperty.call(
                          variant ?? {},
                          'active_price_adjustment'
                        ),
                      }
                    }}
                  >
                    {({ formActivePriceAdjustment, hasFormActivePriceAdjustment }) => {
                      const hasLivePriceAdjustment = Boolean(
                        liveVariant &&
                        Object.prototype.hasOwnProperty.call(liveVariant, 'active_price_adjustment')
                      )
                      const activePriceAdjustment = hasLivePriceAdjustment
                        ? liveVariant?.active_price_adjustment
                        : hasFormActivePriceAdjustment
                          ? formActivePriceAdjustment
                          : draftActivePriceAdjustment

                      if (!activePriceAdjustment) {
                        return null
                      }

                      return activePriceAdjustment.id && canViewPriceChangeLink ? (
                        <PriceChangeInlineHint
                          className="mt-1"
                          data-testid={`variant-price-helper-${index}`}
                        >
                          <Link
                            {...path.priceChanges.show(activePriceAdjustment.id).link}
                            className="inline-flex"
                          >
                            <Typography variant="label" tone="interaction" interaction="link">
                              {getActivePriceAdjustmentPreviewLabel(
                                subField.state.value,
                                activePriceAdjustment
                              )}
                            </Typography>
                          </Link>
                        </PriceChangeInlineHint>
                      ) : (
                        <PriceChangeInlineHint
                          className="mt-1"
                          data-testid={`variant-price-helper-${index}`}
                        >
                          <Typography variant="label" tone="info">
                            {getActivePriceAdjustmentPreviewLabel(
                              subField.state.value,
                              activePriceAdjustment
                            )}
                          </Typography>
                        </PriceChangeInlineHint>
                      )
                    }}
                  </form.Subscribe>
                </>
              )}
            </form.AppField>
          </div>

          <form.Subscribe
            selector={(state) => ({
              addOnMaxQuantity: state.values.variants?.[index]?.add_on_max_quantity ?? null,
              useInventory: Boolean(state.values.variants?.[index]?.use_inventory),
            })}
          >
            {({ addOnMaxQuantity, useInventory }) => (
              <div className="@container/variant-rich-text space-y-4 border-t border-border/60 pt-5">
                <div
                  className={cn(
                    'grid gap-5',
                    !isDesignersChoiceProduct &&
                      !isSubscriptionTier &&
                      '@[40rem]/variant-rich-text:grid-cols-2 @[40rem]/variant-rich-text:items-start'
                  )}
                >
                  <div className="min-w-0">
                    <form.AppField name={`variants[${index}].description_html`}>
                      {(subField) => (
                        <subField.RichTextInput
                          id={`${id}-variants.${index}.description`}
                          label={`${variantNoun} Description`}
                          fallbackHtmlValue={isReadOnly ? productDescriptionHtml : undefined}
                          fallbackPlainTextValue={isReadOnly ? productDescription : undefined}
                          labelEnd={
                            <Typography variant="label" tone="muted" className="ml-2">
                              Optional / Visible to customers
                            </Typography>
                          }
                          labelRowClassName="min-h-8 flex-wrap gap-y-1"
                          plainTextFieldName={`variants[${index}].description`}
                          placeholder="Leave blank to use the product description above."
                          readOnlyEmptyText="No product description added yet."
                        />
                      )}
                    </form.AppField>
                  </div>

                  {!isDesignersChoiceProduct && !isSubscriptionTier ? (
                    <div className="min-w-0 border-t border-border/60 pt-5 @[40rem]/variant-rich-text:border-t-0 @[40rem]/variant-rich-text:pt-0">
                      <form.AppField name={`variants[${index}].recipe_html`}>
                        {(subField) => (
                          <subField.RichTextInput
                            id={`${id}-variants.${index}.recipe`}
                            label="Recipe"
                            labelEnd={
                              <div className="ml-2 inline-flex flex-wrap items-center gap-2">
                                <Typography variant="label" tone="muted">
                                  Optional / Hidden from customers
                                </Typography>
                                {!isReadOnly && recipeSources.length > 0 ? (
                                  <Select
                                    key={recipeCopySelectKey}
                                    onValueChange={(selectedVariantIndex) => {
                                      const selectedRecipeSource = recipeSources.find(
                                        (source) => String(source.index) === selectedVariantIndex
                                      )

                                      if (!selectedRecipeSource) {
                                        return
                                      }

                                      subField.setValue(selectedRecipeSource.recipe_html ?? null)
                                      form.setFieldValue(
                                        `variants[${index}].recipe`,
                                        selectedRecipeSource.recipe
                                      )
                                      setRecipeCopySelectKey((currentKey) => currentKey + 1)
                                    }}
                                  >
                                    <SelectTrigger
                                      aria-label="Copy recipe from another variant"
                                      size="sm"
                                      className="min-w-0 sm:min-w-[11rem]"
                                    >
                                      <SelectValue placeholder="Copy from..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {recipeSources.map((source) => (
                                        <SelectItem
                                          key={`${source.label}-${source.index}`}
                                          value={String(source.index)}
                                        >
                                          {source.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : null}
                              </div>
                            }
                            labelRowClassName="min-h-8 flex-wrap gap-y-1"
                            plainTextFieldName={`variants[${index}].recipe`}
                            placeholder={'3 roses\n2 lilies\n1 premium ribbon'}
                            preserveSpaces
                            readOnlyEmptyText="No recipe added yet."
                          />
                        )}
                      </form.AppField>
                    </div>
                  ) : null}
                </div>

                {!isSubscriptionTier ? (
                  <div className="space-y-5 border-t border-border/60 pt-5">
                    <div className="grid gap-4 md:grid-cols-2">
                      <form.AppField name={`variants[${index}].cost`}>
                        {(subField) => (
                          <subField.MoneyInput
                            id={`${id}-variants.${index}.cost`}
                            label="Your Cost"
                            className="max-w-sm"
                          />
                        )}
                      </form.AppField>
                      <form.AppField name={`variants[${index}].sku`}>
                        {(subField) => (
                          <subField.TextInput
                            id={`${id}-variants.${index}.sku`}
                            label="SKU"
                            placeholder="SKU"
                          />
                        )}
                      </form.AppField>
                      <form.AppField name={`variants[${index}].barcodes`}>
                        {(subField) => (
                          <subField.BarcodeListInput
                            id={`${id}-variants.${index}.barcodes`}
                            className="md:col-span-2"
                          />
                        )}
                      </form.AppField>
                    </div>

                    {isAddOnProduct && (!isReadOnly || addOnMaxQuantity) ? (
                      <form.AppField name={`variants[${index}].add_on_max_quantity`}>
                        {(subField) => (
                          <subField.TextInput
                            id={`${id}-variants.${index}.add-on-max-quantity`}
                            label="Maximum Quantity (per parent product, 10 if left blank)"
                            type="number"
                            min="1"
                            max="10"
                            step="1"
                            readOnlyEmptyText="10"
                            className="max-w-md"
                            labelClassName="whitespace-nowrap"
                            inputClassName="max-w-xs"
                          />
                        )}
                      </form.AppField>
                    ) : null}

                    {!isReadOnly || useInventory ? (
                      <div className="space-y-4 pt-1">
                        {!isReadOnly || useInventory ? (
                          <form.AppField name={`variants[${index}].use_inventory`}>
                            {(subField) => (
                              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex min-w-0 gap-3">
                                  <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
                                    <Boxes className="size-4" aria-hidden="true" />
                                  </span>
                                  <div className="min-w-0 space-y-1">
                                    <Typography variant="cardTitle" className="block pb-0">
                                      Track Inventory
                                    </Typography>
                                    <Typography variant="supporting" className="block max-w-3xl">
                                      Track stock counts for this variant and manage it from the
                                      Inventory page.
                                    </Typography>
                                    {!canTrackInventory ? (
                                      <Typography variant="supporting" className="block">
                                        You must first create an active location to track inventory.{' '}
                                        <Link
                                          {...inventorySetupPath.link}
                                          className="font-medium underline underline-offset-2"
                                        >
                                          Create a location
                                        </Link>
                                      </Typography>
                                    ) : null}
                                  </div>
                                </div>
                                <Label
                                  htmlFor={`${id}-variants.${index}.inventory`}
                                  appearance="choicePill"
                                  tone="muted"
                                >
                                  <span>{subField.state.value ? 'On' : 'Off'}</span>
                                  <Switch
                                    id={`${id}-variants.${index}.inventory`}
                                    name={subField.name}
                                    checked={Boolean(subField.state.value)}
                                    disabled={
                                      isReadOnly || (!canTrackInventory && !subField.state.value)
                                    }
                                    aria-label="Track Inventory"
                                    onCheckedChange={(checked) => {
                                      handleInventoryTrackingChange(checked, subField)

                                      if (checked) {
                                        focusQuantityOnHandInput()
                                      }
                                    }}
                                  />
                                </Label>
                              </div>
                            )}
                          </form.AppField>
                        ) : null}

                        {useInventory ? (
                          <form.AppField name={`variants[${index}].on_hand`}>
                            {(subField) => (
                              <subField.TextInput
                                id={`${id}-variants.${index}.on_hand`}
                                label="Quantity on Hand"
                                type="number"
                                min="0"
                                step="1"
                                disabled={!canTrackInventory}
                                className="max-w-36"
                              />
                            )}
                          </form.AppField>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </form.Subscribe>

          <form.AppField name={`variants[${index}].fulfillment_types`}>
            {(subField) => {
              const fulfillmentTypes = subField.state.value ?? []

              // Option order keeps "off, then on again" equal to the saved value.
              const toggleFulfillmentType = (
                fulfillmentType: VariantFulfillmentType,
                checked: boolean
              ) => {
                subField.handleChange(
                  FULFILLMENT_OPTIONS.map((option) => option.id).filter((type) =>
                    type === fulfillmentType ? checked : fulfillmentTypes.includes(type)
                  )
                )
              }

              return (
                <section className="space-y-4 border-t border-border/60 pt-5">
                  <div className="flex min-w-0 gap-3">
                    <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-interaction dark:bg-primary/15">
                      <Truck className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 space-y-1">
                      <Typography variant="cardTitle" className="block pb-0">
                        Online Fulfillment
                      </Typography>
                      <Typography variant="supporting" className="block max-w-3xl">
                        Choose how online shoppers can receive this item.
                      </Typography>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {FULFILLMENT_OPTIONS.map((option) => {
                      const switchId = `${id}-variants.${index}.fulfillment-${option.id}`
                      const isChecked = fulfillmentTypes.includes(option.id)

                      return (
                        <Label key={option.id} htmlFor={switchId} appearance="choicePill">
                          <span>{option.name}</span>
                          <Switch
                            id={switchId}
                            name={subField.name}
                            checked={isChecked}
                            disabled={isReadOnly}
                            aria-label={option.name}
                            onCheckedChange={(checked) =>
                              toggleFulfillmentType(option.id, Boolean(checked))
                            }
                          />
                        </Label>
                      )
                    })}
                  </div>
                  {fulfillmentTypes.length === 0 ? (
                    <Typography
                      variant="supporting"
                      tone="destructive"
                      role="alert"
                      className="block"
                    >
                      Turn on Pickup, Delivery or both to save this item.
                    </Typography>
                  ) : (
                    <Typography variant="supporting" className="block">
                      At least one fulfillment method must stay selected.
                    </Typography>
                  )}
                </section>
              )
            }}
          </form.AppField>
        </div>

        {!isReadOnly && canDeleteVariant ? (
          <div className="flex justify-end border-t border-border/60 pt-5">
            <ConfirmationDialog
              title={`Delete ${variantNoun}?`}
              variant="destructive"
              description={`Are you sure you want to delete this ${variantNoun.toLowerCase()}?`}
              onConfirm={removeVariant}
              noButtonText
              trigger={
                <Button type="button" variant="destructive" size="sm">
                  <Trash2 className="size-4" />
                  Delete {variantNoun.toLowerCase()}
                </Button>
              }
            />
          </div>
        ) : null}

        {!canDeleteVariant && !isReadOnly && showMinimumOptionNotice ? (
          <div className="border-t border-border/60 pt-5">
            <div className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
              <Typography variant="label" tone="interaction">
                At least one {variantNoun.toLowerCase()} is required.
              </Typography>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default VariantRow
